import { DealerLocator } from "components/DealerLocator";
import { Dealer } from "components/DealerLocator/DealerLocator.interfaces";
import Layout from "components/Layout";
import { useRouterParams } from "components/useRouterParams";
import { readCMSDataClientSide, readCMSDataServerSide } from "helpers/api";
import { CmsImage, CmsLink, StreamfieldBlock } from "interfaces";
import { GetServerSidePropsContext } from "next";
import type {
    EmtekPageProps,
    InferGetWagtailServerSidePropsType,
} from "pages/[[...slug]]";
import { EmtekClientProvider } from "queryClient";
import { useEffect } from "react";

import { Toolbox, ToolboxBlock } from "./Toolbox";

export type PageProps = EmtekPageProps<{
    body: (ToolboxBlock | OnlineDealersBlock)[];
}>;

export interface OnlineDealerCmsData {
    title: string;
    link?: CmsLink;
    logo: CmsImage;
}

export type OnlineDealerBlock = StreamfieldBlock<"dealer", OnlineDealerCmsData>;

export type OnlineDealersBlock = StreamfieldBlock<
    "dealers",
    OnlineDealerBlock[]
>;

const DealerLocatorPage = (
    props: InferGetWagtailServerSidePropsType<typeof fetchData>,
) => {
    useDefaultZip();
    return (
        <EmtekClientProvider>
            <Layout
                metadata={props.wagtail}
                menus={props.menus}
                //  promo={props.promo}
            >
                {props.wagtail.body.map((block, index) => {
                    switch (block.type) {
                        case "dealers":
                            return null;
                        default:
                            return (
                                <Toolbox
                                    block={block}
                                    key={block.id}
                                    isFirstBlock={index === 0}
                                />
                            );
                    }
                })}
                <DealerLocator
                    dealers={props.dealers}
                    onlineDealers={props.wagtail.body.find(
                        (block) => block.type === "dealers",
                    )}
                />
            </Layout>
        </EmtekClientProvider>
    );
};

export default DealerLocatorPage;

const defaultZipStorageKey = "default-zip";
function useDefaultZip() {
    const router = useRouterParams({
        shallow: false,
        scroll: false,
        method: "replace",
    });
    useEffect(() => {
        if (!router.hasParam("q")) {
            const defaultZip = localStorage.getItem(defaultZipStorageKey);
            if (defaultZip) {
                router.setParam("q", defaultZip);
            } else {
                readCMSDataClientSide("/api/auth/guess-geolocation/", {})
                    .then((resp: { postal_code: null | string }) => {
                        if (resp.postal_code) {
                            router.setParam("q", resp.postal_code);
                        }
                    })
                    .catch((e) => {
                        // ok with this failing silently
                        console.error(e);
                    });
            }
        } else {
            localStorage.setItem(
                defaultZipStorageKey,
                router.getParamValue("q") as string,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

export async function fetchData(
    context: GetServerSidePropsContext,
    wagtailPageProps: PageProps,
    apiResponse: Response,
) {
    let searchParams = new URL(
        context.resolvedUrl,
        `https://${context.req.headers.host}`,
    ).searchParams;
    if (!searchParams.get("q")) searchParams.set("q", "");
    if (!searchParams.get("distance")) searchParams.set("distance", "25");
    const dealers: Dealer[] = await readCMSDataServerSide("/api/dealers/", {
        context: context,
        search: searchParams,
    });

    return {
        props: {
            ...wagtailPageProps,
            dealers,
        },
        apiResponse: apiResponse,
    };
}
